/**
 * Version changes between tabs of this browser (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4e).
 *
 * A tab that verifies, fails, discards or grants fixes on a version posts it; the other tabs
 * showing that conversation re-read versions, so their cards and auto-start decisions stay
 * current. Other devices are covered by re-reading versions when a tab becomes visible.
 * `BroadcastChannel` never delivers to the tab that posted.
 */

export const VERSION_EVENTS_CHANNEL = 'ideoz-prototype-versions';

export interface VersionChangeEvent {
  type: 'version-changed';
  conversationId: string;
  llmInteractionId: string;
  status: string;
}

export interface VersionEventChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
}

export function parseVersionChangeEvent(data: unknown): VersionChangeEvent | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (
    record.type !== 'version-changed' ||
    typeof record.conversationId !== 'string' ||
    typeof record.llmInteractionId !== 'string' ||
    typeof record.status !== 'string'
  ) {
    return null;
  }
  return {
    type: 'version-changed',
    conversationId: record.conversationId,
    llmInteractionId: record.llmInteractionId,
    status: record.status,
  };
}

export interface VersionEvents {
  publish(event: Omit<VersionChangeEvent, 'type'>): void;
  /** Events for `conversationId` from other tabs; returns an unsubscribe function. */
  subscribe(conversationId: string, listener: (event: VersionChangeEvent) => void): () => void;
}

export function createVersionEvents(channel: VersionEventChannel | null): VersionEvents {
  return {
    publish(event) {
      try {
        channel?.postMessage({ type: 'version-changed', ...event } satisfies VersionChangeEvent);
      } catch {
        // A closed channel only means other tabs refresh on their next visibility change.
      }
    },
    subscribe(conversationId, listener) {
      if (!channel) return () => undefined;
      const handler = (event: { data: unknown }): void => {
        const parsed = parseVersionChangeEvent(event.data);
        if (parsed && parsed.conversationId === conversationId) listener(parsed);
      };
      channel.addEventListener('message', handler);
      return () => channel.removeEventListener('message', handler);
    },
  };
}

/** The browser channel, or null where `BroadcastChannel` does not exist (tests, old browsers). */
export function browserVersionEventChannel(): VersionEventChannel | null {
  return typeof BroadcastChannel === 'undefined'
    ? null
    : (new BroadcastChannel(VERSION_EVENTS_CHANNEL) as unknown as VersionEventChannel);
}
